import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type CommissionBulkInput, type CommissionRateChange } from "@/api";

// Раздел «Комиссии». Любая правка ставки инвалидирует весь раздел: одна
// ставка категории меняет и таблицу категорий, и строки магазинов, и историю.
export const commissionKeys = {
	all: ["commissions"] as const,
	overview: (days: number) => ["commissions", "overview", days] as const,
	shops: (days: number) => ["commissions", "shops", days] as const,
	category: (id: string, days: number) => ["commissions", "category", id, days] as const,
	history: (filter: { shopId?: string; categoryId?: string }) =>
		["commissions", "history", filter.shopId ?? "", filter.categoryId ?? ""] as const,
};

export function useCommissionOverview(days: number) {
	return useQuery({
		queryKey: commissionKeys.overview(days),
		queryFn: () => api.commissions.overview(days),
	});
}

export function useCommissionShops(days: number) {
	return useQuery({
		queryKey: commissionKeys.shops(days),
		queryFn: () => api.commissions.shops(days),
	});
}

export function useCommissionCategoryShops(categoryId: string | null, days: number) {
	return useQuery({
		queryKey: commissionKeys.category(categoryId ?? "", days),
		queryFn: () => api.commissions.categoryShops(categoryId ?? "", days),
		enabled: !!categoryId,
	});
}

export function useCommissionHistory(filter: { shopId?: string; categoryId?: string }, enabled = true) {
	return useQuery({
		queryKey: commissionKeys.history(filter),
		queryFn: () => api.commissions.history({ ...filter, limit: 50 }),
		enabled,
	});
}

export function useCommissionMutations() {
	const queryClient = useQueryClient();
	const refresh = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: commissionKeys.all }),
			// Финансы и карточки магазинов считают комиссию по тем же ставкам.
			queryClient.invalidateQueries({ queryKey: ["finances"] }),
		]);
	return {
		setBase: useMutation({
			mutationFn: (rate: number) => api.commissions.setBase(rate),
			onSuccess: refresh,
		}),
		setRate: useMutation({
			mutationFn: (change: CommissionRateChange) => api.commissions.setRate(change),
			onSuccess: refresh,
		}),
		bulk: useMutation({
			mutationFn: (input: CommissionBulkInput) => api.commissions.bulk(input),
			onSuccess: refresh,
		}),
	};
}
